import { BuilderView } from 'src/pages/workflow-builder-edit/types';
import { SubflowType } from 'src/types/workflowBuilderAPITypes';

interface BuildPostDuplicateSearchArgs {
  intentId: string;
  intentWorkflowId: string;
  subflowType?: SubflowType;
  targetView?: BuilderView;
  targetWorkflowId?: string | null;
  /**
   * Lazy fetcher for a channel-specific workflow id. Only invoked when the
   * caller requests a target view but the duplicate response did not include
   * that view's workflow id.
   */
  fetchTargetWorkflowId?: () => Promise<string | null | undefined>;
  /**
   * Lazy fetcher for the new intent's voice workflow id. Only invoked for
   * outbound duplicates — non-outbound duplicates skip this network call.
   */
  fetchVoiceWorkflowId: () => Promise<string | null | undefined>;
}

/**
 * Build the search string used to navigate to the workflow editor after a
 * duplicate completes.
 *
 * Outbound (voice_outbound) workflows have no widget tab and run as autoflow,
 * so they route to the Voice autoflow editor with the per-product voice
 * workflow id and `header=outbound` so the back button lands on the correct
 * sub-tab. Everything else opens in the target view, defaulting to widget.
 */
export const buildPostDuplicateSearch = async ({
  fetchTargetWorkflowId,
  fetchVoiceWorkflowId,
  intentId,
  intentWorkflowId,
  subflowType,
  targetView = 'widget',
  targetWorkflowId,
}: BuildPostDuplicateSearchArgs): Promise<string> => {
  if (subflowType === 'voice_outbound') {
    const voiceWorkflowId = (await fetchVoiceWorkflowId()) ?? intentWorkflowId;
    return `workflowId=${voiceWorkflowId}&intentId=${intentId}&view=voice&mode=autoflow&header=outbound`;
  }

  const workflowId =
    targetWorkflowId ?? (await fetchTargetWorkflowId?.()) ?? intentWorkflowId;

  return `workflowId=${workflowId}&intentId=${intentId}&view=${targetView}`;
};
